import {
  Card,
  CardContent,
  CardHeader,
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  Skeleton,
} from '@bpinternal/ui-kit-next'
import { startCase } from 'lodash'
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { useAnalyticsMetric } from '~/features/home/hooks/useAnalyticsMetric'
import { cn, formatters } from '~/utils'
import { useAgentAnalyticsContext } from './AgentAnalyticsContext'
import { AnalyticsMetricSelector, AnalyticsMetricSelectorSkeleton } from './AnalyticsMetricSelector'

type AnalyticsChartProps = {
  botId: string
  workspaceId: string
  className?: string
}

export const AnalyticsChartSkeleton = ({ className }: { className?: string }) => {
  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="space-y-0 border-b md:p-0">
        <AnalyticsMetricSelectorSkeleton />
      </CardHeader>
      <CardContent className="pt-4">
        <Skeleton className="h-64 w-full md:h-[468px]" />
      </CardContent>
    </Card>
  )
}

export const AnalyticsChart = ({ botId, workspaceId, className }: AnalyticsChartProps) => {
  const { selectedMetric, dateShortcut, getLabel } = useAgentAnalyticsContext()

  const metricLabel = getLabel(selectedMetric)
  const { chartData } = useAnalyticsMetric(botId, workspaceId, {
    extractValue: (record) => {
      if (record && typeof record === 'object' && selectedMetric in record) {
        return typeof record[selectedMetric] === 'number' ? record[selectedMetric] : 0
      }
      return 0
    },
    dateShortcut,
    chartDataKey: selectedMetric,
    valueKey: 'analyticsDataValues',
  })

  const chartConfig: ChartConfig = {
    [selectedMetric]: {
      label: metricLabel,
      color: `hsl(var(--chart-1))`,
    },
    [`${selectedMetric}-${dateShortcut}`]: {
      label: `${metricLabel} ${startCase(dateShortcut)}`,
      color: `hsl(var(--chart-2))`,
    },
  } satisfies ChartConfig

  const maxValueCurrent = Math.max(...(chartData.map((item) => item[selectedMetric]) as number[]))
  const maxValuePrevious = Math.max(...(chartData.map((item) => item[`${selectedMetric}-${dateShortcut}`]) as number[]))

  const yAxisDataKey = maxValueCurrent > maxValuePrevious ? selectedMetric : `${selectedMetric}-${dateShortcut}`

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="space-y-0 border-b md:p-0">
        <AnalyticsMetricSelector botId={botId} workspaceId={workspaceId} />
      </CardHeader>
      <CardContent className="pt-4">
        <ChartContainer config={chartConfig} className="aspect-auto h-64 duration-300 md:h-[468px]">
          <AreaChart
            accessibilityLayer
            data={chartData}
            margin={{
              top: 16,
              right: 0,
              bottom: 0,
              left: 16,
            }}
          >
            <defs>
              <linearGradient id={`fill${selectedMetric}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={`var(--color-${selectedMetric})`} stopOpacity={0.8} />
                <stop offset="95%" stopColor={`var(--color-${selectedMetric})`} stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id={`fill${selectedMetric}-${dateShortcut}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={`var(--color-${selectedMetric}-${dateShortcut})`} stopOpacity={0.8} />
                <stop offset="95%" stopColor={`var(--color-${selectedMetric}-${dateShortcut})`} stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} x={48} width={5000} />
            <XAxis
              dataKey="endDateTimeUtc"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(value) => {
                const date = new Date(value)
                const dateString = date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })
                if (dateShortcut !== 'lastDay') {
                  return dateString
                }
                const dateTimeString = date.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: 'numeric',
                })
                return `${dateString}, ${dateTimeString}`
              }}
            />
            <YAxis
              dataKey={yAxisDataKey}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(value) => {
                return formatters.number(value)
              }}
              dx={-32}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(value) => {
                    const date = new Date(value)
                    const dateString = date.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })
                    if (dateShortcut !== 'lastDay') {
                      return dateString
                    }
                    const dateTimeString = date.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: 'numeric',
                    })
                    return `${dateString}, ${dateTimeString}`
                  }}
                  indicator="dot"
                />
              }
            />
            <Area
              type="monotone"
              dataKey={selectedMetric}
              fill={`url(#fill${selectedMetric})`}
              stroke={`var(--color-${selectedMetric})`}
            />
            <Area
              type="monotone"
              dataKey={`${selectedMetric}-${dateShortcut}`}
              fill={`url(#fill${selectedMetric}-${dateShortcut})`}
              stroke={`var(--color-${selectedMetric}-${dateShortcut})`}
            />
            <ChartLegend content={<ChartLegendContent />} className="text-center @xl:hidden" />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
